import {
  TuituiBotClient,
  type TuituiLogger,
} from '@qihoo/tuitui-bot-sdk';

// SSRF Guard 用于阻止远程上传源（即 message 工具输入 URL 后触发 channel.outbound.sendMedia()）访问标准私网、回环地址，并防范 DNS 重绑定和危险重定向。
// 其实际作用有限：
// 部分内网主机使用 11 网段，OpenClaw 不会将其识别为内网；
// 云端部署通常也在受控网络中运行，无需额外防护；
// 即使下载失败，开启 exec 工具权限后，OpenClaw 仍可能尝试通过外部命令下载。
const TUITUI_SSRF_POLICY = { allowedHostnames: ['im.live.360.cn', 'im.qihoo.net'] } as const;

async function loadFetchWithSsrFGuard(): Promise<any> {
  try {
    const ssrfRuntimeImport: any = await import('openclaw/plugin-sdk/ssrf-runtime');
    return (ssrfRuntimeImport.default ?? ssrfRuntimeImport).fetchWithSsrFGuard;
  } catch {
    try {
      const ssrfRuntimeImport: any = await import('openclaw/plugin-sdk/tlon' as string);
      return (ssrfRuntimeImport.default ?? ssrfRuntimeImport).fetchWithSsrFGuard;
    } catch {}
  }
  throw new Error('[tuitui] fetchWithSsrFGuard() API import failed');
}

export const createOpenClawFetchWithSSRF = (auditContext = 'tuitui.upload.download'): typeof globalThis.fetch => {
  return async (url, init) => {
    const fetchWithSsrFGuard = await loadFetchWithSsrFGuard();
    const guarded = await fetchWithSsrFGuard({
      policy: TUITUI_SSRF_POLICY,
      url: String(url),
      init,
      auditContext,
    });

    const { response, release } = guarded;
    try {
      const buffer = await response.arrayBuffer();
      return new Response(buffer, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
    } finally {
      await release?.();
    }
  };
};

export function createTuituiClient(
  account: any,
  logger: TuituiLogger | false = console,
): TuituiBotClient {
  if (!account?.appId || !account?.appSecret) {
    throw new Error('[tuitui] appId and appSecret are required');
  }
  return new TuituiBotClient(account.appId, account.appSecret, {
    fetchWithSSRF: createOpenClawFetchWithSSRF(),
    ...(logger === false ? {} : { logger }),
  });
}
